import { IcoArrowTopRight16, IcoCheckSmall16 } from '@onefootprint/icons';
import * as RadixDropdown from '@radix-ui/react-dropdown-menu';
import { cx } from 'class-variance-authority';
import { cloneElement, forwardRef, isValidElement, type ReactNode, type Ref } from 'react';
import { ShortcutHint } from '../..';
import type { ItemProps } from '../dropdown.types';

const Item = forwardRef<HTMLDivElement, ItemProps>(
  (
    {
      iconLeft: IconLeft,
      iconRight: IconRight,
      children,
      asChild,
      asLink,
      size = 'default',
      destructive,
      onSelect,
      className,
      variant = 'default',
      value,
      shortcutHint,
      ...props
    },
    ref,
  ) => {
    const RightIcon = asLink ? IcoArrowTopRight16 : IconRight;

    // With asChild the caller's element becomes the row itself rather than being nested inside it.
    // Nesting leaves the row's icon and padding outside the element, so clicking there hits the
    // bare row: for a link that means the menu closes and the navigation never happens.
    const child =
      asChild && isValidElement<{ className?: string; children?: ReactNode; ref?: Ref<HTMLDivElement> }>(children)
        ? children
        : null;

    const sharedStyles = cx(
      'relative flex w-full overflow-hidden cursor-pointer rounded-default bg-elevated gap-3 box-border flex items-center justify-between',
      'data-[disabled]:text-quaternary data-[disabled]:cursor-auto data-[disabled]:select-none data-[disabled]:hover:bg-transparent',
      'focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary',
      'focus:outline-none',
      {
        'hover:bg-(--dropdown-hover-bg) text-primary': !destructive,
        'hover:bg-error text-error': destructive,
        'px-3 py-2 text-body-2 h-9': size === 'default',
        'px-3 py-2 text-body-2 h-8': size === 'compact',
        'px-3 py-1 text-caption-2 h-7': size === 'tiny',
      },
      child?.props.className,
      className,
    );

    const content = (
      <>
        {IconLeft && (
          <span className="shrink-0 flex items-center">
            <IconLeft color={destructive ? 'error' : undefined} />
          </span>
        )}
        <div className="flex items-center w-full truncate text-decoration-none">
          {child ? child.props.children : children}
        </div>
        {RightIcon && (
          <span className="shrink-0 flex items-center">
            <RightIcon color={asLink ? 'secondary' : undefined} />
          </span>
        )}
        {shortcutHint && <ShortcutHint keys={shortcutHint} />}
      </>
    );

    if (variant === 'radio') {
      return (
        <RadixDropdown.RadioItem {...props} value={value || ''} onSelect={onSelect} asChild>
          <div ref={ref} className={sharedStyles}>
            {content}
            <RadixDropdown.ItemIndicator>
              <IcoCheckSmall16 />
            </RadixDropdown.ItemIndicator>
          </div>
        </RadixDropdown.RadioItem>
      );
    }

    return (
      <RadixDropdown.Item {...props} onSelect={onSelect} asChild>
        {child ? (
          cloneElement(child, { ref, className: sharedStyles }, content)
        ) : (
          <div ref={ref} className={sharedStyles}>
            {content}
          </div>
        )}
      </RadixDropdown.Item>
    );
  },
);

export default Item;
